<template>
  <div class="home">
    <div>
      <!-- header -->
      <section>
        <div class="py-8 px-8">
          <div class="d-flex flex-wrap align-items-center justify-content-between">
            <div class="col-12 col-lg-auto mb-3 mb-lg-0">
              <div class="d-flex align-items-center">
                <h4 class="mb-0 me-2">{{$t('payroll_management')}}</h4>
              </div>
            </div>
            <div class="col-12 col-lg-auto d-flex align-items-center gap-2">
              <div class="input-group flex-grow-1" style="border-bottom: 1px solid #ddd;">
                <input class="form-control border-0 search-input" autocomplete="off" type="text" v-model="search" :placeholder="$t('search_employee')">
              </div>
              <!-- Mobile filter toggle -->
              <button @click="showFilters = !showFilters" class="btn btn-outline-secondary d-md-none">
                <AppIcon name="bi-funnel" class="bi bi-funnel" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Summary Cards -->
      <section class="px-8 mb-4">
        <div class="row g-2">
          <div class="col-4 col-md-4">
            <div class="card text-center">
              <div class="card-body py-2 px-2">
                <AppIcon name="bi-cash-stack" class="bi bi-cash-stack text-success" style="font-size: 1.5rem" />
                <h5 class="mt-1 mb-1">{{ formatMoney(stats.totalCost) }}</h5>
                <small class="text-muted" style="font-size: 0.75rem">{{$t('total_payroll_cost')}} ({{ currentMonthName }})</small>
              </div>
            </div>
          </div>
          <div class="col-4 col-md-4">
            <div class="card text-center">
              <div class="card-body py-2 px-2">
                <AppIcon name="bi-hourglass-split" class="bi bi-hourglass-split text-warning" style="font-size: 1.5rem" />
                <h5 class="mt-1 mb-1">{{ stats.pendingCount }}</h5>
                <small class="text-muted" style="font-size: 0.75rem">{{$t('pending_payments')}}</small>
              </div>
            </div>
          </div>
          <div class="col-4 col-md-4">
            <div class="card text-center">
              <div class="card-body py-2 px-2">
                <AppIcon name="bi-check2-all" class="bi bi-check2-all text-success" style="font-size: 1.5rem" />
                <h5 class="mt-1 mb-1">{{ stats.paidCount }}</h5>
                <small class="text-muted" style="font-size: 0.75rem">{{$t('paid_employees')}}</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- table -->
      <section class="">
        <div class="px-8">
          <!-- Mobile action buttons -->
          <div class="d-flex d-md-none gap-2 mb-3">
            <button v-if="helper.checkAccess('create_payrolls') != -1" @click="create" class="btn btn-sm btn-success flex-grow-1">
              <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
              <span>{{$t('create_payroll')}}</span>
            </button>
          </div>

          <!-- Filters section -->
          <div class="row align-items-end g-2 mb-4" :class="{'d-none d-md-flex': !showFilters}">
            <!-- Month Filter -->
            <div class="col-12 col-md-3 col-lg-2">
              <label class="form-label small d-md-none mb-1">{{$t('month')}}</label>
              <input type="month" v-model="filters.month" class="form-control form-control-sm">
            </div>

            <!-- Status filter -->
            <div class="col-12 col-md-3 col-lg-2">
              <label class="form-label small d-md-none mb-1">{{$t('status')}}</label>
              <select v-model="filters.status" class="form-select form-select-sm">
                <option value="">{{$t('all_statuses')}}</option>
                <option value="draft">{{$t('draft')}}</option>
                <option value="paid">{{$t('paid')}}</option>
              </select>
            </div>
            
            <!-- Spacer for desktop -->
            <div class="col d-none d-md-block"></div>
            
            <!-- Desktop action buttons -->
            <div class="col-md-2 d-none d-md-block" v-if="helper.checkAccess('create_payrolls') != -1">
              <button @click="create" class="btn btn-sm btn-success w-100">
                <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                <span>{{$t('create_payroll')}}</span>
              </button>
            </div>
          </div>

          <div class="page-table-container mb-6 bg-white rounded shadow">
            
            <!-- table -->
            <div class="table-responsive">
              <table class="table mb-0 table-borderless table-striped small">
                <thead>
                  <tr class="text-secondary">
                    <th class="pt-4 pb-3 px-6">{{$t('employee')}}</th>
                    <sortable-table-header field="month" label="period" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchPayrolls)" />
                    <sortable-table-header field="basic_salary" label="basic_salary" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchPayrolls)" />
                    <sortable-table-header field="net_salary" label="net_salary" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchPayrolls)" />
                    <sortable-table-header field="status" label="status" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchPayrolls)" />
                    <sortable-table-header field="payment_date" label="payment_date" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, fetchPayrolls)" />
                    <th class="pt-4 pb-3 px-6">{{$t('action')}}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="payroll in payrolls" :key="payroll.id">
                    <td class="py-5 px-6">
                      <div class="d-flex align-items-center">
                        <div class="avatar-circle me-2 bg-primary text-white d-flex align-items-center justify-content-center" style="width: 32px; height: 32px; border-radius: 50%; font-size: 12px;">
                          <img v-if="payroll.employee && payroll.employee.image" :src="payroll.employee.image" class="w-100 h-100 rounded-circle" style="object-fit: cover;">
                          <span v-else>{{ getInitials(payroll.employee ? payroll.employee.name : '?') }}</span>
                        </div>
                        <router-link v-if="payroll.employee" :to="`/hr/employees/${payroll.employee.id}`" class="text-decoration-none text-dark fw-bold">
                          {{payroll.employee.name}}
                        </router-link>
                        <span v-else>-</span>
                      </div>
                    </td>
                    <td class="py-5 px-6">{{ formatPeriod(payroll.month) }}</td>
                    <td class="py-5 px-6">{{ formatMoney(payroll.basic_salary) }}</td>
                    <td class="py-5 px-6">
                      <router-link :to="`/hr/payroll/${payroll.id}`" class="fw-bold text-primary text-decoration-none">
                        {{ formatMoney(payroll.net_salary) }}
                      </router-link>
                    </td>
                    <td class="py-5 px-6">
                      <span class="badge rounded-pill" :class="{
                        'bg-success': payroll.status === 'paid',
                        'bg-secondary': payroll.status === 'draft'
                      }">
                        {{$t(payroll.status)}}
                      </span>
                    </td>
                    <td class="py-5 px-6">{{ payroll.payment_date ? formatDate(payroll.payment_date) : '-' }}</td>
                    <td class="py-5 px-6">
                      <button @click="() => $router.push(`/hr/payroll/${payroll.id}`)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-eye" class="bi bi-eye" />
                        {{$t('details')}}
                      </button>
                      <button v-if="payroll.status !== 'paid' && helper.checkAccess('edit_payrolls') != -1" @click="edit(payroll)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-pencil" class="bi bi-pencil" />
                        {{$t('edit')}}
                      </button>
                      <button v-if="payroll.status !== 'paid' && helper.checkAccess('delete_payrolls') != -1" @click="deletePayroll(payroll)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-trash" class="bi bi-trash" />
                        {{$t('delete')}}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="total > payrolls.length" style="text-align:center;margin-top:1rem;margin-bottom:1rem">
              <compact-pagination :pages="pagination" :current-page="currentPage" @change="changePage" />
            </div>

            <table-loading-bar v-if="loading" class="page-table-state text-center py-5" />
            <div v-else-if="!payrolls.length && !isFiltered" class="page-table-state text-center py-5 my-5 bg-white rounded shadow-sm border" style="border-style: dashed !important;">
              <AppIcon name="bi-wallet2" class="bi bi-wallet2 text-muted" style="font-size: 3rem;" />
              <h4 class="mt-3">{{ $t('no_payrolls_added') }}</h4>
              <p class="text-muted">{{ $t('start_by_adding_payrolls') }}</p>
              <template v-if="helper.checkAccess('create_payrolls') != -1">
                  <button class="btn btn-outline-primary mt-2" @click="create">
                      {{ $t('create_first_payroll') }}
                  </button>
              </template>
            </div>
            <div v-else-if="!payrolls.length && isFiltered" class="page-table-state text-center py-5 text-muted">
               <AppIcon name="bi-wallet2" class="bi bi-wallet2 fs-1 d-block mb-2" />
               {{$t('no_payroll_records')}}.
            </div>
          </div>
          <!-- table -->

          <div class="col-sm-7">
            {{$t('show')}}
            <select v-model="perPage" class="form-select form-select-sm d-inline-block w-auto mx-2" style="cursor:pointer;">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            {{$t('entities')}}
          </div>
        </div>
      </section>
    </div>

    <!-- Payroll Modal -->
    <div class="modal fade" id="payrollModal" tabindex="-1" aria-labelledby="payrollModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-light">
            <h5 class="modal-title" id="payrollModalLabel">{{isEdit ? $t('edit_payroll') : $t('create_payroll')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body p-4">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label fw-bold small text-uppercase text-muted">{{$t('employee')}} <span class="text-danger">*</span></label>
                <select v-model="form.employee_id" @change="onEmployeeChange" class="form-select" :disabled="isEdit">
                  <option value="">{{$t('select_employee')}}</option>
                  <option v-for="emp in employees" :key="emp.id" :value="emp.id">{{emp.name}}</option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label fw-bold small text-uppercase text-muted">{{$t('period')}} <span class="text-danger">*</span></label>
                <input type="month" v-model="form.month" class="form-control">
              </div>
            </div>

            <div class="mb-4">
              <label class="form-label fw-bold small text-uppercase text-muted">{{$t('basic_salary')}}</label>
              <div class="input-group">
                <input type="number" v-model.number="form.basic_salary" class="form-control" @input="calculateNetSalary">
                <span class="input-group-text">{{ currency }}</span>
              </div>
            </div>

            <div class="card border mb-4">
              <div class="card-header bg-light">
                <strong>Données obligatoires du bulletin marocain</strong>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label class="form-label small">Secteur</label>
                    <select v-model="form.payroll_context.sector" class="form-select">
                      <option value="non_agricultural">Non agricole</option>
                      <option value="agricultural">Agricole</option>
                    </select>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label small">N° CNSS employeur</label>
                    <input v-model.trim="form.payroll_context.employer_cnss_number" class="form-control" maxlength="100">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label small">N° CNSS salarié</label>
                    <input v-model.trim="form.payroll_context.employee_cnss_number" class="form-control" maxlength="100">
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label small">Catégorie professionnelle</label>
                    <input v-model.trim="form.payroll_context.professional_category" class="form-control" maxlength="255">
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="form-label small">Personnes à charge (0–6)</label>
                    <input type="number" min="0" max="6" v-model.number="form.payroll_context.dependents_count" @input="calculateNetSalary" class="form-control">
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="form-label small">Jours payés</label>
                    <input type="number" min="0" max="31" step="0.5" v-model.number="form.payroll_context.paid_days" class="form-control">
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="form-label small">Heures normales</label>
                    <input type="number" min="0" max="400" step="0.01" v-model.number="form.payroll_context.regular_hours" class="form-control">
                  </div>
                  <div class="col-md-3 mb-3">
                    <label class="form-label small">Heures supplémentaires</label>
                    <input type="number" min="0" max="200" step="0.01" v-model.number="form.payroll_context.overtime_hours" class="form-control">
                  </div>
                  <div class="col-md-3 mb-3" v-if="Number(form.payroll_context.overtime_hours) > 0">
                    <label class="form-label small">Taux horaire supplémentaire (MAD)</label>
                    <input type="number" min="0" step="0.01" v-model.number="form.payroll_context.overtime_rate" class="form-control">
                  </div>
                </div>
                <label class="form-check mb-2">
                  <input type="checkbox" v-model="form.payroll_context.seniority_acknowledged" class="form-check-input">
                  <span class="form-check-label">Le traitement de la prime d'ancienneté a été vérifié (incluse, ajoutée ou exemption documentée).</span>
                </label>
                <div class="small text-muted">
                  Les numéros CNSS, la catégorie, les jours et les heures sont requis avant de marquer la paie comme payée.
                </div>
              </div>
            </div>

            <div class="row">
              <!-- Allowances -->
              <div class="col-md-6 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label fw-bold small text-uppercase text-muted mb-0">{{$t('allowances')}}</label>
                  <button @click="addAllowance" class="btn btn-xs btn-outline-success py-0" style="font-size: 10px;">
                    <AppIcon name="bi-plus" class="bi bi-plus" /> {{$t('add')}}
                  </button>
                </div>
                <div v-for="(allowance, index) in form.allowances" :key="'allowance-'+index" class="mb-3">
                 <div class="input-group">
                  <input type="text" v-model="allowance.description" @input="calculateNetSalary" class="form-control form-control-sm" :placeholder="$t('description')">
                  <input type="number" v-model.number="allowance.amount" class="form-control form-control-sm" style="max-width: 100px;" @input="calculateNetSalary" placeholder="0.00">
                  <button @click="removeAllowance(index)" class="btn btn-outline-danger btn-sm"><AppIcon name="bi-x" class="bi bi-x" /></button>
                 </div>
                 <div class="d-flex gap-3 mt-1 small">
                   <label><input type="checkbox" v-model="allowance.taxable" @change="calculateNetSalary"> Imposable à l'IR</label>
                   <label><input type="checkbox" v-model="allowance.subject_to_social_contributions" @change="calculateNetSalary"> Soumise CNSS/AMO</label>
                 </div>
                </div>
                <div v-if="form.allowances.length === 0" class="text-muted small fst-italic">{{$t('no_allowances')}}</div>
              </div>

              <!-- Deductions -->
              <div class="col-md-6 mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <label class="form-label fw-bold small text-uppercase text-muted mb-0">{{$t('deductions')}}</label>
                  <button @click="addDeduction" class="btn btn-xs btn-outline-danger py-0" style="font-size: 10px;">
                    <AppIcon name="bi-plus" class="bi bi-plus" /> {{$t('add')}}
                  </button>
                </div>
                <div v-for="(deduction, index) in form.deductions" :key="'deduction-'+index" class="input-group mb-2">
                  <input type="text" v-model="deduction.description" @input="calculateNetSalary" class="form-control form-control-sm" :placeholder="$t('description')">
                  <input type="number" v-model.number="deduction.amount" class="form-control form-control-sm" style="max-width: 100px;" @input="calculateNetSalary" placeholder="0.00">
                  <button @click="removeDeduction(index)" class="btn btn-outline-danger btn-sm"><AppIcon name="bi-x" class="bi bi-x" /></button>
                </div>
                <div v-if="form.deductions.length === 0" class="text-muted small fst-italic">{{$t('no_deductions')}}</div>
              </div>
            </div>

            <div class="card bg-light border-0 mb-3">
              <div class="card-body p-3">
                <div class="d-flex justify-content-between align-items-center">
                  <span class="fw-bold">{{$t('net_salary')}}</span>
                  <h4 class="mb-0 text-primary">{{ formatMoney(form.net_salary) }}</h4>
                </div>
                <div v-if="preview.breakdown" class="row mt-3 small">
                  <div class="col-4">IR: <strong>{{ formatMoney(preview.tax_amount) }}</strong></div>
                  <div class="col-4">CNSS/AMO: <strong>{{ formatMoney(preview.social_security_amount) }}</strong></div>
                  <div class="col-4">Règles: <strong>{{ preview.breakdown.rules_version }}</strong></div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label fw-bold small text-uppercase text-muted">{{$t('status')}}</label>
                <select v-model="form.status" class="form-select">
                  <option value="draft">{{$t('draft')}}</option>
                  <option value="paid">{{$t('paid')}}</option>
                </select>
              </div>
              <div class="col-md-6 mb-3" v-if="form.status === 'paid'">
                <label class="form-label fw-bold small text-uppercase text-muted">{{$t('payment_method')}}</label>
                <select v-model="form.payment_method" class="form-select">
                  <option value="">{{$t('select_payment_method')}}</option>
                  <option value="transfer">{{$t('transfer')}}</option>
                  <option value="cash">{{$t('cash')}}</option>
                  <option value="check">{{$t('check')}}</option>
                  <option value="bank_deposit">{{$t('bank_deposit')}}</option>
                </select>
              </div>
            </div>

            <div class="row" v-if="form.status === 'paid'">
              <div class="col-md-12 mb-3">
                <label class="form-label fw-bold small text-uppercase text-muted">{{$t('payment_date')}}</label>
                <input type="date" v-model="form.payment_date" class="form-control">
              </div>
            </div>

            <div class="mb-3">
              <collapsible-notes-field v-model="form.notes" id="payroll-notes" rows="2" :placeholder="$t('enter_notes')" />
            </div>
          </div>
          <div class="modal-footer bg-light">
            <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">{{$t('cancel')}}</button>
            <button type="button" class="btn btn-success" @click="save">{{$t('save')}}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '@/helper'
import sortableList from '@/mixins/sortableList'

export default {
  name: 'HrPayroll',
  persistedFilterState: {
    key: 'hr-payroll',
    fields: ['search', 'filters', 'perPage', 'sortField', 'sortDirection'],
  },
  mixins: [sortableList],
  data() {
    return {
      helper,
      payrolls: [],
      employees: [],
      search: '',
      showFilters: false,
      filters: {
        month: this.localDate().slice(0, 7),
        status: ''
      },
      currentPage: 1,
      perPage: 10,
      total: 0,
      isEdit: false,
      form: this.getEmptyForm(),
      modal: null,
      loading: false,
      pagination: [],
      currency: 'MAD',
      preview: {},
      previewTimer: null,
      generatingPayrolls: false,
      stats: { totalCost: 0, pendingCount: 0, paidCount: 0 }
    }
  },
  computed: {
    currentMonthName() {
      if (!this.filters.month) return ''
      const [year, month] = this.filters.month.split('-')
      const date = new Date(year, month - 1)
      return date.toLocaleString('default', { month: 'long', year: 'numeric' })
    },
    isFiltered() {
      return (this.search && this.search.length > 0) ||
             (this.filters.month && this.filters.month !== this.localDate().slice(0, 7)) ||
             (this.filters.status && this.filters.status !== '');
    }
  },
  watch: {
    search() {
      this.fetchPayrolls()
    },
    'filters.month'() {
      this.fetchPayrolls()
      this.fetchStats()
    },
    'filters.status'() {
      this.fetchPayrolls()
    },
    perPage() {
      this.fetchPayrolls()
    }
  },
  mounted() {
    //check permissions
    if(helper.checkAccess('view_payrolls') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_payrolls') == -2) return this.$router.push({path : '/subscribe'})

    this.fetchPayrolls()
    this.fetchEmployees()
    this.fetchStats()
    // eslint-disable-next-line no-undef
    this.modal = new bootstrap.Modal(document.getElementById('payrollModal'), {backdrop: 'static', keyboard: false})
  },
  methods: {
    getInitials(name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
    },
    async fetchStats() {
      try {
        let url = `${process.env.VUE_APP_API}/hr/insights/payroll-stats?`
        if (this.filters.month) url += `month=${this.filters.month}`
        const response = await helper.customGet(url)
        this.stats = {
          totalCost: response.data.totalCost || 0,
          pendingCount: response.data.pendingCount || 0,
          paidCount: response.data.paidCount || 0
        }
      } catch (error) {
        alert(error)
      }
    },
    localDate() {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Africa/Casablanca',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(new Date())
    },
    getEmptyForm() {
      const business = this.$store?.getters?.BUSINESS_DETAILS || {}
      const metadata = business.metadata || {}
      return {
        employee_id: '',
        month: this.localDate().slice(0, 7),
        basic_salary: 0,
        allowances: [],
        deductions: [],
        net_salary: 0,
        status: 'draft',
        payment_method: '',
        payment_date: '',
        notes: '',
        payroll_context: {
          sector: 'non_agricultural',
          dependents_count: 0,
          employer_cnss_number: metadata?.hr?.cnss_number || metadata?.cnss_number || '',
          employee_cnss_number: '',
          professional_category: '',
          regular_hours: null,
          overtime_hours: 0,
          overtime_rate: null,
          paid_days: null,
          seniority_acknowledged: false
        }
      }
    },
    formatDate(date) {
      return helper.formatDate(date)
    },
    formatMoney(amount) {
      return new Intl.NumberFormat('fr-MA', {
        style: 'currency',
        currency: 'MAD'
      }).format(Number(amount) || 0)
    },
    formatPeriod(monthStr) {
      if (!monthStr) return '-'
      const [year, month] = monthStr.split('-')
      const date = new Date(year, month - 1)
      return date.toLocaleString('default', { month: 'long', year: 'numeric' })
    },
    async fetchEmployees() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/employees?per_page=1000`)
        this.employees = response.data.data || []
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message
        })
      }
    },
    async fetchPayrolls() {
      try {
        this.loading = true
        this.payrolls = []
        
        const params = new URLSearchParams({ page: this.currentPage, per_page: this.perPage })
        this.appendSortParams(params)
        if (this.search) params.append('keyword', this.search)
        if (this.filters.month) params.append('month', this.filters.month)
        if (this.filters.status) params.append('status', this.filters.status)
        const url = `${process.env.VUE_APP_API}/payrolls?${params.toString()}`

        const response = await helper.customGet(url)
        
        this.payrolls = response.data.data || []
        this.total = response.data.count || 0
        
        // Build pagination array
        this.pagination = []
        let maxPagination = Math.ceil(this.total / this.perPage)
        for(let i = 1; i <= maxPagination; i++){
          this.pagination.push(i)
        }
        
        this.loading = false
      } catch (error) {
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: error.message
        })
        this.payrolls = []
        this.total = 0
        this.loading = false
      }
    },
    create() {
      this.isEdit = false
      this.form = this.getEmptyForm()
      this.preview = {}
      this.modal.show()
    },
    edit(payroll) {
      this.isEdit = true
      // Parse allowances and deductions if they are strings (JSON)
      let allowances = payroll.allowances
      let deductions = payroll.deductions
      
      if (typeof allowances === 'string') {
        try { allowances = JSON.parse(allowances) } catch(e) { allowances = [] }
      }
      if (typeof deductions === 'string') {
        try { deductions = JSON.parse(deductions) } catch(e) { deductions = [] }
      }
      let metadata = {}
      try {
        metadata = typeof payroll.metadata === 'string' ? JSON.parse(payroll.metadata || '{}') : (payroll.metadata || {})
      } catch (e) {
        metadata = {}
      }
      const emptyContext = this.getEmptyForm().payroll_context

      this.form = {
        id: payroll.id,
        employee_id: payroll.employee_id,
        month: payroll.month,
        basic_salary: parseFloat(payroll.basic_salary),
        allowances: Array.isArray(allowances) ? allowances : [],
        deductions: Array.isArray(deductions) ? deductions : [],
        net_salary: parseFloat(payroll.net_salary),
        status: payroll.status,
        payment_method: payroll.payment_method || '',
        payment_date: payroll.payment_date ? payroll.payment_date.split('T')[0] : '',
        notes: payroll.notes || '',
        payroll_context: {
          ...emptyContext,
          ...(metadata.payroll_context || {})
        }
      }
      this.preview = {
        net_salary: parseFloat(payroll.net_salary),
        tax_amount: parseFloat(payroll.tax_amount),
        social_security_amount: parseFloat(payroll.social_security_amount),
        breakdown: metadata.breakdown || {}
      }
      this.modal.show()
    },
    onEmployeeChange() {
      const employee = this.employees.find(e => e.id === this.form.employee_id)
      if (employee) {
        this.form.basic_salary = parseFloat(employee.salary || 0)
        this.form.payroll_context.professional_category = employee.position || ''
        this.form.payroll_context.employee_cnss_number = employee.cnss_number || ''
        this.form.payroll_context.dependents_count = Number(employee.dependents_count) || 0
        this.calculateNetSalary()
      }
    },
    addAllowance() {
      this.form.allowances.push({
        description: '',
        amount: 0,
        taxable: true,
        subject_to_social_contributions: true
      })
    },
    removeAllowance(index) {
      this.form.allowances.splice(index, 1)
      this.calculateNetSalary()
    },
    addDeduction() {
      this.form.deductions.push({ description: '', amount: 0 })
    },
    removeDeduction(index) {
      this.form.deductions.splice(index, 1)
      this.calculateNetSalary()
    },
    calculateNetSalary() {
      clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(() => this.refreshPayrollPreview(), 250)
    },
    async refreshPayrollPreview() {
      if (!this.form.month || Number(this.form.basic_salary) < 0) return
      const allowances = this.form.allowances.filter(item => item.description)
      const deductions = this.form.deductions.filter(item => item.description)
      try {
        const response = await helper.customPost(
          `${process.env.VUE_APP_API}/payrolls/calculate`,
          {
            basic_salary: Number(this.form.basic_salary) || 0,
            month: this.form.month,
            dependents_count: Number(this.form.payroll_context.dependents_count) || 0,
            allowances: JSON.stringify(allowances),
            deductions: JSON.stringify(deductions)
          },
          { showToast: false }
        )
        this.preview = response.data
        this.form.net_salary = response.data.net_salary
      } catch (error) {
        this.preview = {}
      }
    },
    async save() {
      try {
        if (!this.form.employee_id || !this.form.month) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: this.$t('please_fill_required_fields')
          })
          return
        }

        const data = {
          employee_id: this.form.employee_id,
          month: this.form.month,
          basic_salary: this.form.basic_salary,
          allowances: JSON.stringify(this.form.allowances.filter(item => item.description || Number(item.amount) > 0)),
          deductions: JSON.stringify(this.form.deductions.filter(item => item.description || Number(item.amount) > 0)),
          payroll_context: this.form.payroll_context,
          status: this.form.status,
          payment_method: this.form.status === 'paid' ? this.form.payment_method : null,
          payment_date: this.form.status === 'paid' ? (this.form.payment_date || this.localDate()) : null,
          notes: this.form.notes
        }

        this.loading = true
        if (this.isEdit) {
          await helper.customPatch(`${process.env.VUE_APP_API}/payrolls/${this.form.id}`, data, {showToast: true})
        } else {
          await helper.customPost(`${process.env.VUE_APP_API}/payrolls`, data, {showToast: true})
        }
        this.loading = false
        this.form = this.getEmptyForm()
        this.modal.hide()
        this.fetchPayrolls()
      } catch (error) {
        this.loading = false
        const warnings = error?.response?.data?.compliance_warnings || []
        this.$swal({
          icon: 'error',
          title: this.$t('error'),
          text: warnings.length ? warnings.join(' • ') : (error?.response?.data?.message || error.message)
        })
      }
    },
    async deletePayroll(payroll) {
      const result = await this.$swal.fire({
        title: this.$t('are_you_sure'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('delete')
      })
      
      if (result.isConfirmed) {
        try {
          await helper.customDelete(`${process.env.VUE_APP_API}/payrolls/${payroll.id}`)
          this.payrolls = helper.withoutDeletedRecords(this.payrolls, payroll.id)
          this.total = Math.max(0, this.total - 1)
        } catch (error) {
          this.$swal({
            icon: 'error',
            title: this.$t('error'),
            text: error.message
          })
        }
      }
    },
    changePage(page) {
      this.currentPage = page
      this.fetchPayrolls()
    }
  }
}
</script>

<style scoped>
.avatar-circle {
  transition: all 0.3s ease;
}
.table-responsive {
  overflow-x: auto;
}
.card {
  transition: transform 0.2s;
}
.card:hover {
  transform: translateY(-2px);
}
</style>
